import * as React from "react";
import { fetchDefaultKnowledgeBase } from "../api/knowledgeBase";
import { fetchAssistantModels } from "../api/assistant";
import { listChatSessions } from "../api/chat";
import type { AvailableChatModelsResponse, ChatSession } from "../types/chat";
import type { KnowledgeBase } from "../types/knowledgeBase";
import { promiseAllSettled } from "../utils/promiseAllSettled";

/** Human-readable label for each parallel bootstrap request (logging / debugging). */
export const CHAT_BOOTSTRAP_KEYS = ["defaultKb", "models", "sessions"] as const;

export type ChatBootstrapKey = (typeof CHAT_BOOTSTRAP_KEYS)[number];

export type ChatBootstrapState = {
  loading: boolean;
  loaded: boolean;
  /** First parallel wave finished (KB + models + sessions). */
  primarySettled: boolean;
  /** Kept for compatibility; add-in history is loaded in `ChatPanel` via `getAddInChatMessageHistory`. */
  messagesSettled: boolean;
  defaultKb: KnowledgeBase | null;
  defaultKbError: string | null;
  models: AvailableChatModelsResponse | null;
  modelsError: string | null;
  sessions: ChatSession[];
  sessionsError: string | null;
  /** First session in the list when sessions loaded successfully. */
  activeSessionId: string | null;
};

const initialState: ChatBootstrapState = {
  loading: false,
  loaded: false,
  primarySettled: false,
  messagesSettled: false,
  defaultKb: null,
  defaultKbError: null,
  models: null,
  modelsError: null,
  sessions: [],
  sessionsError: null,
  activeSessionId: null,
};

function reasonToString(reason: unknown): string {
  if (reason instanceof Error) {
    return reason.message;
  }
  return String(reason);
}

/**
 * Capabilities → Assistant models (same as Hub Ask Cenna).
 * Mapped into the composer’s ``AvailableChatModelsResponse`` shape.
 */
async function fetchAskCennaModels(): Promise<AvailableChatModelsResponse> {
  const res = await fetchAssistantModels();
  return {
    models: res.models.map((m) => ({
      id: m.id,
      label: m.label,
      provider: m.provider || m.backend || "",
    })),
    defaultModel: res.default || "",
  };
}

/**
 * Runs parallel settle: default KB, available models, session list.
 * Each branch settles independently (same semantics as `Promise.allSettled`; polyfilled for Office WebView).
 */
async function runPrimaryBootstrap(): Promise<
  Pick<
    ChatBootstrapState,
    | "defaultKb"
    | "defaultKbError"
    | "models"
    | "modelsError"
    | "sessions"
    | "sessionsError"
    | "activeSessionId"
  >
> {
  const [kbSettled, modelsSettled, sessionsSettled] = await promiseAllSettled([
    fetchDefaultKnowledgeBase(),
    fetchAskCennaModels(),
    listChatSessions(1, 100),
  ]);

  const defaultKb =
    kbSettled.status === "fulfilled" ? (kbSettled.value as KnowledgeBase) : null;
  const defaultKbError = kbSettled.status === "rejected" ? reasonToString(kbSettled.reason) : null;

  const models =
    modelsSettled.status === "fulfilled" ? (modelsSettled.value as AvailableChatModelsResponse) : null;
  const modelsError = modelsSettled.status === "rejected" ? reasonToString(modelsSettled.reason) : null;

  const sessions =
    sessionsSettled.status === "fulfilled" ? (sessionsSettled.value as ChatSession[]) : [];
  const sessionsError = sessionsSettled.status === "rejected" ? reasonToString(sessionsSettled.reason) : null;

  const activeSessionId = sessions[0]?.id ?? null;

  return {
    defaultKb,
    defaultKbError,
    models,
    modelsError,
    sessions,
    sessionsError,
    activeSessionId,
  };
}

/**
 * When the Chat tab becomes active, loads default KB, models, and sessions in parallel.
 * Add-in message history is fetched separately in `ChatPanel`.
 * Pass ``enabled=false`` to skip network when Ask Cenna is gated off.
 */
export function useChatBootstrap(active: boolean, enabled = true): ChatBootstrapState {
  const [state, setState] = React.useState<ChatBootstrapState>(initialState);

  React.useEffect(() => {
    if (!active || !enabled) {
      setState({ ...initialState, loaded: !enabled && active, primarySettled: !enabled && active, messagesSettled: !enabled && active });
      return undefined;
    }

    let cancelled = false;

    void (async () => {
      setState((s) => ({
        ...s,
        loading: true,
        loaded: false,
        primarySettled: false,
        messagesSettled: false,
        defaultKbError: null,
        modelsError: null,
        sessionsError: null,
      }));

      try {
        const primary = await runPrimaryBootstrap();
        if (cancelled) {
          return;
        }

        setState((s) => ({
          ...s,
          ...primary,
          primarySettled: true,
          messagesSettled: true,
          loading: false,
          loaded: true,
        }));
      } catch (e) {
        if (!cancelled) {
          setState((s) => ({
            ...s,
            loading: false,
            loaded: true,
            primarySettled: true,
            messagesSettled: true,
            modelsError: s.modelsError ?? reasonToString(e),
          }));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [active, enabled]);

  return state;
}
